import React from "react";
import styles from "./dateFilter.module.css";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/**
 * Reusable year / month / day filter.
 *
 * Controlled component: pass `value` ({ year, month, day }) and `onChange`.
 * - Pick a Year to filter the whole year.
 * - Add a Month to narrow to that month (requires a year).
 * - Add a Day to narrow to that day (requires a month).
 * Lower levels are disabled until the level above them is set, and clearing a
 * higher level clears the levels below it. Empty string = "not filtered".
 */
const DateFilter = ({ value = {}, onChange, startYear }) => {
  const { year = "", month = "", day = "" } = value;

  const currentYear = new Date().getFullYear();
  const firstYear = startYear || currentYear - 5;
  const years = [];
  for (let y = currentYear; y >= firstYear; y--) years.push(y);

  // Days available depend on the selected month/year (handles 28–31).
  const daysInMonth =
    year && month ? new Date(Number(year), Number(month), 0).getDate() : 31;
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const update = (patch) => {
    const next = { year, month, day, ...patch };
    // Clearing a higher level clears everything below it.
    if (patch.year === "") {
      next.month = "";
      next.day = "";
    }
    if (patch.month === "") {
      next.day = "";
    }
    onChange(next);
  };

  return (
    <div className={styles.filter}>
      <label className={styles.field}>
        <span className={styles.label}>Year</span>
        <select
          className="form-select"
          value={year}
          onChange={(e) => update({ year: e.target.value })}
        >
          <option value="">All time</option>
          {years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </label>

      <label className={styles.field}>
        <span className={styles.label}>Month</span>
        <select
          className="form-select"
          value={month}
          disabled={!year}
          onChange={(e) => update({ month: e.target.value })}
        >
          <option value="">Whole year</option>
          {MONTHS.map((m, i) => (
            <option key={m} value={i + 1}>{m}</option>
          ))}
        </select>
      </label>

      <label className={styles.field}>
        <span className={styles.label}>Day</span>
        <select
          className="form-select"
          value={day}
          disabled={!month}
          onChange={(e) => update({ day: e.target.value })}
        >
          <option value="">Whole month</option>
          {days.map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
      </label>
    </div>
  );
};

export default DateFilter;
